import { connect } from 'react-redux';
import { useField } from 'react-final-form';

import { IDecisionActForm } from 'DecisionPackage/MainModule/interfaces/IDecisionActForm';
import { KnmActExtract } from 'KnmPackage/KnmPaperlessActModule/KnmActExtract/KnmActExtract';
import { KnmActGeneral } from 'KnmPackage/KnmPaperlessActModule/KnmActGeneral/KnmActGeneral';
import { KnmActParticipants } from 'KnmPackage/KnmPaperlessActModule/KnmActParticipants/KnmActParticipants';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { KnmActEvents } from 'KnmPackage/KnmPaperlessActModule/KnmActEvents/KnmActEvents';
import {
  getDecisionDataId,
  getDecisionFormRef,
  getDecisionOnSaveButtonClick,
  getDecisionPositionsSelectOptions,
  getKnmIsDecisionStatusFillInProcess,
  getIsDecisionReasonValidate,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { KnmActIntroductions } from 'KnmPackage/KnmPaperlessActModule/KnmActIntroductions/KnmActIntroductions';
import { KnmActRemarks } from 'KnmPackage/KnmPaperlessActModule/KnmActRemarks/KnmActRemarks';
import { KnmActAdditional } from 'KnmPackage/KnmPaperlessActModule/KnmActAdditional/KnmActAdditional';
import { KnmActSignInfo } from 'KnmPackage/KnmPaperlessActModule/KnmActSignInfo/KnmActSignInfo';
import { KnmActSignButton } from 'KnmPackage/KnmPaperlessActModule/KnmActSignButton/KnmActSignButton';
import { IDecisionOrganizationForm } from 'DecisionPackage/MainModule/interfaces/IDecisionOrganizationForm';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { DecisionViolationEvidence } from 'DecisionPackage/DecisionViolationEvidenceModule/components/DecisionViolationEvidence';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import { DecisionActViolations } from './DecisionActViolations/DecisionActViolations';
import { DecisionActSubject } from './DecisionActSubject/DecisionActSubject';
import { IKnmErknmFormObject } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';
import { IDictionaryPositionSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { IDictionaryParticipantPositionSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryParticipantPositionSelectOption';

interface IDecisionPaperlessActItemProps {
  basisDocumentDateTime: string | undefined;
  created: string | undefined;
  inspectors: IKnmErknmFormInspector[] | undefined;
  isDisabledByDirty?: boolean;
  isViolationFieldsWatchOnly: boolean;
  isWatchOnly: boolean;
  knoOrganization: IDictionaryKnoOrganizationSelectOption | undefined;
  name: string;
  objects?: IKnmErknmFormObject[];
  organization: IDecisionOrganizationForm | undefined;
  supervisionType: IDictionarySupervisionTypeSelectOption | undefined;
  value: IDecisionActForm;
}

interface IDecisionPaperlessActItemStoreProps {
  decisionId: number | undefined;
  decisionPositionsSelectOptions:
    | IDictionaryPositionSelectOption[]
    | IDictionaryParticipantPositionSelectOption[]
    | undefined;
  formRef: IDecisionFormRef | undefined;
  isDecisionStatusFillInProcess: boolean;
  isDecisionReasonValidate: boolean;
  onSaveButtonClick: (() => void) | undefined;
  settings: IAdminSetting[];
}

function DecisionPaperlessActItemWithoutStore({
  basisDocumentDateTime,
  created,
  inspectors,
  isDisabledByDirty = false,
  isViolationFieldsWatchOnly,
  isWatchOnly,
  knoOrganization,
  name,
  objects,
  organization,
  supervisionType,
  value,
  decisionId,
  decisionPositionsSelectOptions,
  formRef,
  isDecisionStatusFillInProcess,
  isDecisionReasonValidate,
  onSaveButtonClick,
  settings,
}: IDecisionPaperlessActItemProps & IDecisionPaperlessActItemStoreProps) {
  const isDisabledField = isWatchOnly || !!value.signed || isDisabledByDirty;
  const prefixId = 'act';

  const events = useField<IKnmErknmFormEvent[]>('events')?.input?.value;

  const isUseEvidence = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_EVIDENCE_4_1_25_STARTS_FROM,
  );
  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );

  const isUseSigners = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ADD_NEW_SIGNER_199_4_1_6_STARTS_FROM,
  );

  const reasons = formRef?.current.getFieldState('reasons')?.value;

  const isPrescriptionCheck = reasons?.[0].reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK;
  const decisionMinHoldingDateTime = isDecisionReasonValidate
    ? reasons?.[0].reason?.prescriptionDate || undefined
    : basisDocumentDateTime;

  return (
    <>
      <KnmActExtract extract={value.document} withoutMarginTop />
      <KnmActGeneral
        name={name}
        isDisabledField={isDisabledField}
        prefixId={prefixId}
        isDecision
        extractVersion={value.extractVersion}
        signActDateTime={value.signActDateTime}
        decisionMinHoldingDateTime={decisionMinHoldingDateTime}
      />
      <DecisionActSubject organization={organization} isDecision />
      <KnmActParticipants
        id={prefixId}
        isDecision
        inspectorsErknm={inspectors}
        isDisabledField={isDisabledField}
        name={name}
        expertsErknm={[]}
        formRef={formRef}
      />
      {events.length > 0 && (
        <KnmActEvents
          id={prefixId}
          name={name}
          isDisabledField={isDisabledField}
          isDecision
          formRef={formRef}
          isPaperless={value.paperless}
          value={value}
        />
      )}

      {isUseEvidence && (
        <DecisionViolationEvidence id={prefixId} name={name} formRef={formRef} isWatchOnly={isDisabledField} />
      )}
      <DecisionActViolations
        id={prefixId}
        watchOnly={isViolationFieldsWatchOnly}
        name={name}
        value={value}
        isDisabledField={isDisabledField}
        isDisabledByDirty={isDisabledByDirty}
        knoOrganization={knoOrganization}
        supervisionType={supervisionType}
        objects={objects}
        isEnablePrescriptionCheck={isEnablePrescriptionCheck}
        isPrescriptionCheck={isPrescriptionCheck}
      />
      <KnmActSignInfo
        formRef={formRef}
        id={isUseSigners ? `organization.${prefixId}` : prefixId}
        isDisabledField={isDisabledField}
        name={name}
        inspectors={inspectors}
        isDecision
        isErp={false}
        value={value}
        isUseSigners={isUseSigners}
        positionsSelectOptions={decisionPositionsSelectOptions}
      />
      {value.document && (
        <>
          <KnmActIntroductions
            isDecision
            id={`organization.${prefixId}`}
            name={name}
            formRef={formRef}
            isDisabledByDirty={isDisabledByDirty}
          />
          <KnmActRemarks
            isDecision
            id={`organization.${prefixId}`}
            name={name}
            formRef={formRef}
            isDisabledByDirty={isDisabledByDirty}
            holdingDateTime={value.holdingDateTime}
          />
          {value.fileInvalidate && <KnmActAdditional name={name} attachment={value.fileInvalidate} isDecision />}
        </>
      )}
      <KnmActSignButton
        value={value}
        formRef={formRef}
        onSaveButtonClick={onSaveButtonClick}
        isDecision
        isStatusFillInProcess={isDecisionStatusFillInProcess}
        isEnablePrescriptionCheck={isEnablePrescriptionCheck}
        decisionId={decisionId}
        name={name}
      />
    </>
  );
}

const mapStateToProps = (state: IRootState): IDecisionPaperlessActItemStoreProps => {
  return {
    decisionId: getDecisionDataId(state),
    decisionPositionsSelectOptions: getDecisionPositionsSelectOptions(state),
    formRef: getDecisionFormRef(state),
    isDecisionStatusFillInProcess: getKnmIsDecisionStatusFillInProcess(state),
    isDecisionReasonValidate: getIsDecisionReasonValidate(state),
    onSaveButtonClick: getDecisionOnSaveButtonClick(state),
    settings: getAdminFetchSettings(state),
  };
};

export const DecisionPaperlessActItem = connect(mapStateToProps)(DecisionPaperlessActItemWithoutStore);
